import { useEffect } from 'react'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { WizardShell } from '@/components/onboarding/wizard/WizardShell'
import { WizardShellProvider, useWizardShell } from '@/components/onboarding/wizard/WizardShellContext'
import { useUserDisplay } from '@/hooks/use-user-display'
import { APP_HOME_PATH } from '@/lib/app-home'
import { isDemoModeActive } from '@/lib/demo-mode'
import { authReady } from '@/stores/auth.store'
import { useCurrentUser } from '@/stores/user.store'
import {
  listMyOrganizationsQueryOptions,
  organizationActions,
  organizationStore,
  reconcileSelectedOrganization,
  seedSelectedOrganizationFromStorage,
  useMyOrganizations,
} from '@/stores/organization.store'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async ({ context }) => {
    // Onboarding is outside `_app`, so it seeds and reconciles the org itself.
    // Without the reconcile, a member whose stored org is missing or stale
    // would land on "Create your organization" as if they had none.
    const seededOrganizationId = seedSelectedOrganizationFromStorage()

    if (!isDemoModeActive()) {
      await authReady
      if (!context.auth.state.session) return
    }

    // fetchQuery, not ensureQueryData: creating an org invalidates this list,
    // and a cached pre-create list would make the new org look stale.
    const orgsResponse = await context.queryClient
      .fetchQuery(listMyOrganizationsQueryOptions())
      .catch(() => null)
    if (!orgsResponse) return

    reconcileSelectedOrganization(orgsResponse.organizations, seededOrganizationId, context.queryClient)
  },
  component: OnboardingLayout,
})

/**
 * Persistent shell for every onboarding screen (the user profile step and the
 * grouped wizard). Rendering `WizardShell` once here — rather
 * than in each screen — keeps the sidebar mounted across navigations instead
 * of flickering on every route change.
 */
function OnboardingLayout() {
  return (
    <WizardShellProvider>
      <OnboardingShell />
    </WizardShellProvider>
  )
}

function OnboardingShell() {
  const { state, onJumpToGroup } = useWizardShell()
  const { handleLogout } = useUserDisplay()
  const { user, hasLoaded } = useCurrentUser()
  const { organizations } = useMyOrganizations()
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)

  // Nothing downstream works without a name, so an incomplete profile always
  // goes back to the profile step first.
  const navigate = useNavigate()
  useEffect(() => {
    if (!hasLoaded || !user) return
    if (user.firstName && user.lastName) return
    void navigate({ to: '/onboarding/user' })
  }, [hasLoaded, navigate, user])

  // `_app`'s billing gate decides where the newly selected org belongs: an
  // onboarded org opens the app, an incomplete one comes back here.
  const handleSwitchOrganization = (organizationId: string) => {
    organizationActions.setSelectedOrganization(organizationId)
    void navigate({ to: APP_HOME_PATH })
  }

  return (
    <WizardShell
      state={state}
      onJumpToGroup={onJumpToGroup}
      onSignOut={handleLogout}
      organizations={organizations}
      currentOrganizationId={selectedOrganizationId}
      onSwitchOrganization={handleSwitchOrganization}
    >
      <Outlet />
    </WizardShell>
  )
}
